import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { Icon, notification, Modal } from 'antd'
import { RouteComponentProps, useLocation } from 'react-router-dom'
import { TFunction } from 'i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import {
  Avatar,
  BudgetAllocationsCheck,
  BudgetChartBudget,
  BudgetChartBudgets,
  BudgetChartTicket,
  BudgetChartTicketStandardType,
  PolicyRule,
  Request,
  RequestTicket,
  RequestToClassify
} from 'src/types'

import { ApplicationState } from 'src/store'
import { PageHeader, Text } from 'src/components/Ant'
import { resetAir } from 'src/core/store/ducks/air/actions'
import { resetHotels } from 'src/core/store/ducks/hotels/actions'
import { resetVehicles } from 'src/core/store/ducks/vehicles/actions'
import {
  saveFullRequest,
  setDraft
} from 'src/core/store/ducks/requests/actions'
import { stringify } from 'src/core/hooks/use-translation'
import useDocumentTitle from 'src/hooks/use-document-title'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'

import {
  getBudgetAllocationsCheck,
  requestStandardSharingsReduce
} from './components/Steps/Assignment/components/Budget/budget-summary'
import Assignment from './components/Steps/Assignment'
import Itineraries from './components/Steps/Itineraries'
import Services from './components/Steps/Services'
import Setup from './components/Steps/Setup'
import Summary from './components/Steps/Summary'

interface Params {
  reason_id: string
}

type StepKey = 'setup' | 'itineraries' | 'assignment' | 'services' | 'summary'

type StepAlertType = 'error' | 'info' | 'success' | 'warning'

type StepAlertStatus = 'error' | 'finish' | 'process' | 'wait'

interface Step {
  alert: StepAlert
  key: StepKey
  status: StepAlertStatus
  title: string
  renderContent(): React.ReactNode
  validate?({
    budgets,
    policyRule,
    request,
    t
  }: {
    budgets: BudgetChartBudgets[]
    policyRule: PolicyRule
    request: Request
    t: TFunction
  }): StepAlert
}

interface StepAlert {
  message: string
  options?: { [key: string]: string | number }
  type: StepAlertType
}

interface SetBudgetAllocationsProps {
  budgets?: BudgetChartBudget[]
  clear?: boolean
  standard_type?: BudgetChartTicketStandardType
  tickets?: BudgetChartTicket[]
}

interface StepsContextValues {
  budgetChart: BudgetChartBudgets[]
  budgetChartAllocations: BudgetAllocationsCheck
  clearAlerts: () => void
  current: number
  goTo(_: number): void
  next(): void
  policyRule: PolicyRule
  prev(): void
  request: Request
  resetDraft(): void
  saving: boolean
  setAlert(k: StepKey, _: StepAlert): void
  setBudgetAllocations(props: SetBudgetAllocationsProps): void
  setCurrent(_: number): void
  setRequest(_: Request): void
  steps: Step[]
}

const initialSteps: Step[] = [
  {
    alert: null,
    key: 'setup',
    status: 'process',
    title: 'web.setup.title',
    renderContent: () => <Setup />
  },
  {
    alert: null,
    key: 'itineraries',
    status: 'wait',
    title: 'web.itinerary.title',
    renderContent: () => <Itineraries />
  },
  {
    alert: null,
    key: 'assignment',
    status: 'wait',
    title: 'web.assignment.title',
    renderContent: () => <Assignment />,
    validate: ({ budgets, policyRule = {}, request, t }) => {
      const {
        enable_billing_observation = false,
        enable_budget = false,
        enable_standard_billing = false,
        enable_standard_sharing = false
      } = policyRule

      if (enable_standard_sharing) {
        const totalPercent =
          request.request_standard_assignments.request_standard_sharings.reduce(
            (acc, cur) => acc + cur.percentage,
            0
          )

        if (totalPercent !== 100) {
          return {
            message: t('web.assignment.sharing.incomplete'),
            type: 'error'
          }
        }
      }

      if (enable_standard_billing) {
        if (
          request.request_standard_assignments.request_standard_billings
            .length > 1
        ) {
          return {
            message: t('web.assignment.billing.need-have-only-one'),
            type: 'error'
          }
        }

        if (
          enable_billing_observation &&
          request.request_standard_assignments.request_standard_billings
            .length !== 1
        ) {
          return {
            message: t('web.assignment.billing.need-have-one'),
            type: 'error'
          }
        }
      }

      if (enable_budget) {
        const foundViolatedBudget = budgets
          .filter(({ action }) => action === 'BLOCK')
          .find(({ budget_violated }) => budget_violated)

        if (foundViolatedBudget) {
          return {
            message: t('common.budget.violated', {
              text: t('common.block')
            }),
            type: 'error'
          }
        }
      }
      return null
    }
  },
  {
    alert: null,
    key: 'services',
    status: 'wait',
    title: 'web.services.title',
    renderContent: () => <Services />,
    validate: ({ budgets, policyRule, request, t }) => {
      if (
        policyRule?.disable_advance_retroactive && // has policy
        request.requested_on <
          moment({
            hour: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0
          }).unix() && // is antecedence
        request.request_tickets.some(
          ticket => ticket.request_ticket_type === 'ADVANCE'
        ) // has advance
      ) {
        return {
          message: t('web.advance.message.disable-retroactive-advance'),
          type: 'error'
        }
      }

      if (policyRule?.enable_budget) {
        const foundViolatedBudget = budgets
          .filter(({ action }) => action === 'BLOCK')
          .find(({ budget_violated }) => budget_violated)

        if (foundViolatedBudget) {
          return {
            message: t('common.budget.violated', {
              text: t('common.block')
            }),
            type: 'error'
          }
        }
      }
      return null
    }
  },
  {
    alert: null,
    key: 'summary',
    status: 'wait',
    title: 'web.summary.title',
    renderContent: () => <Summary />
  }
]

const draft: Request = {
  contract_id: '',
  cost: 0,
  date_end: moment()
    .set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
    .add(1, 'day')
    .unix(),
  full_request_air: [],
  full_request_hotel: [],
  full_request_vehicle: [],
  justification: '',
  obs: '',
  organization_id: '',
  request_custom_data: [],
  request_id: '',
  request_itineraries: [],
  request_number: '',
  request_standard_assignments: {
    request_standard_billings: [],
    request_standard_sharings: []
  },
  request_tickets: [],
  request_type: 'TRIP',
  request_user_data: [],
  requested_on: moment()
    .set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
    .unix(),
  requester: null,
  status: 'APPROVAL_PENDING',
  ticket_types: [],
  title: ''
}

const initialValues: StepsContextValues = {
  budgetChart: [],
  budgetChartAllocations: {
    active: false,
    budgets: [],
    organization_id: -1,
    policy_rule_id: -1,
    tickets: []
  },
  clearAlerts() {},
  current: 0,
  goTo() {},
  next() {},
  policyRule: undefined,
  prev() {},
  request: null,
  resetDraft() {},
  saving: false,
  setAlert(k: StepKey, _: StepAlert) {},
  setBudgetAllocations(props: SetBudgetAllocationsProps) {},
  setCurrent(_: number) {},
  setRequest(_: Request) {},
  steps: initialSteps
}

const StepsContext = createContext<StepsContextValues>(initialValues)

function useStepsContext(): StepsContextValues {
  return useContext(StepsContext)
}

function getStatus(index: number, toStepNumber: number): StepAlertStatus {
  if (index < toStepNumber) {
    return 'finish'
  }

  if (index > toStepNumber) {
    return 'wait'
  }
  return 'process'
}

function Form({
  history,
  match: {
    params: { reason_id }
  }
}: RouteComponentProps<Params>): JSX.Element {
  const {
    me: {
      organization: { organization_id },
      user
    },
    requests
  } = useSelector((state: ApplicationState) => ({
    me: state.me,
    requests: state.requests
  }))

  const [budgetChartAllocations, setBudgetChartAllocations] =
    useState<BudgetAllocationsCheck>(initialValues.budgetChartAllocations)
  const [budgetChart, setBudgetChart] = useState<BudgetChartBudgets[]>(
    initialValues.budgetChart
  )
  const [current, setCurrent] = useState<number>(initialValues.current)
  const [request, setRequest] = useState<Request>(initialValues.request)
  const [saveError, setSaveError] = useState<string>('')
  const [saving, setSaving] = useState<boolean>(initialValues.saving)
  const [steps, setSteps] = useState<Step[]>(initialValues.steps)

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const policyRule = useReasonPolicyRule(reason_id)
  const location = useLocation<{
    request_tickets?: RequestTicket[]
    request_for_classification?: RequestToClassify
  }>()
  const {
    request_tickets: request_ticket_for_classification,
    request_for_classification
  } = location?.state ?? {}
  useDocumentTitle('web.new-request.title-page')
  /**
   * Find draft
   */
  useEffect(() => {
    setTimeout(() => {
      // if (
      //   !request &&
      //   !!requests.draft &&
      //   `${requests.draft.tmp_reason_id}` === reason_id
      // ) {
      //   Modal.confirm({
      //     cancelText: t('common.actions.no'),
      //     content: t('web.new-request.draft.content'),
      //     okButtonProps: {
      //       //@ts-ignore
      //       'data-test-id': 'draft-action'
      //     },
      //     okText: t('common.actions.yes'),
      //     title: t('web.new-request.draft.title'),
      //     onCancel() {
      //       resetDraft()
      //       dispatch(resetAir())
      //       dispatch(resetHotels())
      //       dispatch(resetVehicles())
      //     },
      //     onOk() {
      //       setRequest({
      //         ...requests.draft,
      //         full_request_air: [],
      //         full_request_hotel: [],
      //         full_request_vehicle: [],
      //         request_tickets: []
      //       })
      //       dispatch(resetAir())
      //       dispatch(resetHotels())
      //       dispatch(resetVehicles())

      //       if (policyRule?.enable_budget) {
      //         const allocationsDraft: BudgetAllocationsCheck = {
      //           active: true,
      //           budgets: [],
      //           organization_id,
      //           policy_rule_id: policyRule?.policy_rule_id,
      //           tickets: []
      //         }

      //         if (
      //           requests.draft.request_standard_assignments
      //             .request_standard_sharings
      //         ) {
      //           allocationsDraft.budgets =
      //             requests.draft.request_standard_assignments.request_standard_sharings.reduce(
      //               requestStandardSharingsReduce,
      //               []
      //             )
      //         } else if (user.cost_center?.cost_center_id) {
      //           allocationsDraft.budgets = user.cost_center.budgets?.map(
      //             budget => ({
      //               budget_id: budget.budget_id,
      //               cost_center_id: user.cost_center.cost_center_id,
      //               share: 100
      //             })
      //           )
      //         }
      //         setBudgetChartAllocations(allocationsDraft)
      //       }
      //     }
      //   })
      //   goTo(0)
      //   return
      // }

      resetDraft()
      dispatch(resetAir())
      dispatch(resetHotels())
      dispatch(resetVehicles())

      const requester: Avatar = {
        active: user.active,
        birthdate: user.birthdate,
        gender: user.gender,
        name: `${user.first_name} ${user.last_name}`,
        user_id: user.user_id,
        vat_number: user.vat_number
      }

      let request_tickets: RequestTicket[] = []

      if (request_ticket_for_classification?.length) {
        request_tickets = request_ticket_for_classification
      }
      if (request_for_classification) {
        const requestUser = request_for_classification.user
          ? request_for_classification.user
          : user

        const { costCenter, contract, reason } = request_for_classification

        draft.date_end = request_for_classification.date_end
        draft.justification = request_for_classification.justification || ''
        draft.obs = request_for_classification.obs || ''
        draft.request_base_id = request_for_classification.request_base_id
        draft.request_user_data = [
          {
            cost_center: costCenter,
            included: true,
            reason_id: reason?.reason_id,
            reason,
            request_user_data_id: null,
            traveler: {
              gender: requestUser.gender,
              name: `${requestUser.first_name} ${requestUser.last_name}`,
              user_id: requestUser.user_id
            },
            user_id: requestUser.user_id
          }
        ]

        draft.requested_on = request_for_classification.requested_on
        draft.title = request_for_classification.title
        draft.request_standard_assignments = {
          request_standard_billings: [],
          request_standard_sharings: [
            {
              active: true,
              contract_id: contract.contract_id,
              contract,
              cost_center_id: costCenter.cost_center_id,
              cost_center: costCenter,
              percentage: 100,
              request_id: null,
              request_standard_sharing_id: null,
              request_standard_sharing_number: null,
              request_ticket_sharing_type: 'UNIFIED',
              tmp_id: Math.random()
            }
          ]
        }
      }

      setRequest({
        ...draft,
        organization_id,
        request_tickets,
        requester,
        tmp_reason_id: reason_id
      })
      goTo(0)
    }, 500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Filter steps by policies
   */
  useEffect(() => {
    if (policyRule) {
      setSteps(() =>
        initialValues.steps.filter(item => {
          // return item.key === 'services' || item.key === 'summary'
          if (item.key === 'itineraries' && !policyRule.enable_itinerary) {
            return false
          }
          if (
            item.key === 'assignment' &&
            !policyRule.enable_standard_billing &&
            !policyRule.enable_standard_sharing
          ) {
            return false
          }
          if (
            item.key === 'services' &&
            !policyRule.enable_advance &&
            !policyRule.enable_flight &&
            !policyRule.enable_hotel &&
            !policyRule.enable_vehicle_rent
          ) {
            return false
          }
          if (item.key === 'summary' && !policyRule.enable_resume) {
            return false
          }

          return true
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyRule])

  useEffect(() => {
    if (policyRule?.enable_budget) {
      let budgets: BudgetChartBudget[] = []
      if (
        requests.draft?.request_standard_assignments.request_standard_sharings
      ) {
        budgets =
          requests.draft.request_standard_assignments.request_standard_sharings.reduce(
            requestStandardSharingsReduce,
            []
          )
      } else if (user.cost_center?.cost_center_id) {
        if (user.cost_center.budgets?.length) {
          budgets = user.cost_center.budgets.map(budget => ({
            budget_id: budget.budget_id,
            cost_center_id: user.cost_center.cost_center_id,
            share: 100
          }))
        } else {
          budgets = [
            {
              cost_center_id: user.cost_center.cost_center_id,
              share: 100
            }
          ]
        }
      }
      setBudgetChartAllocations({
        active: true,
        budgets,
        organization_id,
        policy_rule_id: policyRule.policy_rule_id,
        tickets: []
      })
      return
    }

    setBudgetChart(initialValues.budgetChart)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization_id, policyRule])

  useEffect(() => {
    if (requests.saveFullRequestLoading !== saving) {
      setSaving(requests.saveFullRequestLoading)

      if (!requests.saveFullRequestLoading && !requests.saveFullRequestError) {
        notification.success({
          duration: 10,
          message: requests.saveFullRequestSuccess,
          placement: 'bottomRight'
        })
        resetDraft()
        history.push('/my-requests')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.saveFullRequestLoading])

  useEffect(() => {
    if (requests.saveFullRequestError !== saveError) {
      setSaveError(requests.saveFullRequestError)

      setAlert(
        steps[current].key,
        requests.saveFullRequestError
          ? {
              message: requests.saveFullRequestError,
              type: 'error'
            }
          : null
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.saveFullRequestError])

  /**
   * Go to specific step
   */
  function goTo(toStepNumber: number): void {
    if (!saving && toStepNumber >= 0 && toStepNumber < current) {
      setSteps(oldSteps =>
        oldSteps.map((value: Step, index: number) => ({
          ...value,
          alert: null as StepAlert,
          status: getStatus(index, toStepNumber)
        }))
      )
      setCurrent(toStepNumber)
    }
  }

  /**
   * Set request into StepsContext and persist draft where not found id
   */
  function handleSetRequest(values: Request) {
    setRequest({ ...values })

    if (!values.request_id) {
      dispatch(setDraft({ ...values }))
    }
  }

  /**
   * Go to next step
   */
  function next() {
    if (steps[current].validate) {
      const alert = steps[current].validate({
        budgets: budgetChart,
        policyRule,
        request,
        t
      })

      if (alert) {
        setAlert(steps[current].key, alert)
        return
      }
    }

    if (!saving) {
      if (current < steps.length - 1) {
        const nextStep = current + 1

        const copySteps = [...steps]
        copySteps[current].alert = null
        copySteps[current].status = 'finish'
        copySteps[nextStep].status = 'process'

        setSteps(copySteps)
        setCurrent(nextStep)
      } else {
        Modal.confirm({
          cancelText: t('common.actions.cancel'),
          content: t('web.new-request.modal-accept'),
          okText: t('common.actions.yes'),
          onCancel() {},
          onOk() {
            dispatch(saveFullRequest(request))
          },
          title: t('common.are-you-sure')
        })
      }
    }
  }

  /**
   * Go to prev step
   */
  function prev() {
    if (!saving && current > 0) {
      const prevStep = current - 1

      const copySteps = [...steps]
      copySteps[current].alert = null
      copySteps[current].status = 'wait'
      copySteps[prevStep].status = 'process'

      setSteps(copySteps)
      setCurrent(prevStep)
    }
  }

  function clearAlerts() {
    setSteps(oldSteps =>
      oldSteps.map(step => Object.assign(step, { alert: '' }))
    )
  }

  function setAlert(step: StepKey, alert: StepAlert) {
    const stepIndex = steps.findIndex(item => item.key === step)

    const copySteps = [...steps]
    copySteps[stepIndex].alert = alert

    setSteps(copySteps)
  }

  /**
   * Set base Budget Graph to check
   * the getBudgetAllocationsCheck is a consequence of change
   */
  const setBudgetAllocations = useCallback(
    async (props: SetBudgetAllocationsProps) => {
      if (!budgetChartAllocations.active) {
        return
      }

      setBudgetChartAllocations(oldBudgets => {
        const budget_allocations = { ...oldBudgets }
        if (props.budgets) {
          budget_allocations.budgets = props.budgets
        }

        if (props.tickets) {
          if (props.standard_type) {
            budget_allocations.tickets = [
              ...budget_allocations.tickets.filter(
                ({ standard_type }) => standard_type !== props.standard_type
              ),
              ...props.tickets
            ]
          } else {
            budget_allocations.tickets = props.tickets
          }
        }

        return budget_allocations
      })
    },
    [budgetChartAllocations.active]
  )

  useEffect(() => {
    const { active, policy_rule_id } = budgetChartAllocations
    let isSubscribed = true
    if (active && policy_rule_id > -1) {
      getBudgetAllocationsCheck({
        ...budgetChartAllocations,
        active: undefined
      }).then(barCharts => {
        if (isSubscribed) {
          setBudgetChart(barCharts)
        }
      })
    }
    return () => {
      isSubscribed = false
    }
  }, [budgetChartAllocations])

  /**
   * To reset Draft
   */
  function resetDraft() {
    const requester: Avatar = {
      active: user.active,
      birthdate: user.birthdate,
      gender: user.gender,
      name: `${user.first_name} ${user.last_name}`,
      user_id: user.user_id,
      vat_number: user.vat_number
    }
    setRequest({ ...draft, organization_id, requester })
    dispatch(setDraft(null))
  }

  return (
    <StepsContext.Provider
      value={{
        budgetChart,
        budgetChartAllocations,
        clearAlerts,
        current,
        policyRule,
        goTo,
        next,
        prev,
        request,
        resetDraft,
        saving,
        setAlert,
        setBudgetAllocations,
        setCurrent,
        setRequest: handleSetRequest,
        steps
      }}
    >
      {!!request && (
        <>
          <PageHeader
            extra={[
              <Icon
                key="request-helps"
                onClick={() =>
                  Modal.info({
                    content: (
                      <Text>
                        <Text title="web.new-request.infos.content" />
                        <Text code>shift+ctrl+alt+arrow-left</Text>
                        <Text title="common.actions.or" />
                        <Text code>shift+ctrl+alt+arrow-right</Text>.
                      </Text>
                    ),
                    okText: t('common.actions.close'),
                    onOk() {},
                    title: t('web.new-request.infos.title')
                  })
                }
                type="question-circle"
              />
            ]}
            title={
              !!request && !!request.request_id
                ? stringify('web.new-request.title-section-id-title', {
                    id: request.request_id,
                    title: request.title
                  })
                : 'web.new-request.title-section'
            }
          />
          {steps[current].renderContent()}
        </>
      )}
    </StepsContext.Provider>
  )
}

export default Form

export { StepsContext, useStepsContext }
